import { useTranslation } from 'react-i18next';
import { Palette, Type } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Tabs } from '../ui/Tabs';
import { Input } from '../ui/Input';
import { ColorPickerField } from './ColorPickerField';
import { RichTextArea } from './RichTextArea';
import { TemplateGallery } from './TemplateGallery';
import { TEMPLATE_DEFINITIONS } from './templates';
import type { EmailTemplateType, ColorConfig, ContentField } from './types';

interface EditorPanelProps {
  templateType: EmailTemplateType;
  subject: string;
  greeting: string;
  bodyHtml: string;
  ctaText: string;
  ctaUrl: string;
  colors: ColorConfig;
  selectTemplate: (type: EmailTemplateType) => void;
  setSubject: (v: string) => void;
  setGreeting: (v: string) => void;
  setBodyHtml: (v: string) => void;
  setCtaText: (v: string) => void;
  setCtaUrl: (v: string) => void;
  setColor: (key: keyof ColorConfig, value: string) => void;
  allowedTemplates?: EmailTemplateType[];
}

export function EditorPanel(props: EditorPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('content');

  // Hide content fields the chosen template can't actually use, so the
  // editor stops promising things that don't take effect downstream.
  const visibleFields = useMemo<Set<ContentField>>(() => {
    const def = TEMPLATE_DEFINITIONS.find(td => td.type === props.templateType);
    return new Set(def?.contentFields || ['subject', 'greeting', 'bodyHtml', 'cta']);
  }, [props.templateType]);

  const tabs = [
    { key: 'content', label: t('emailEditor.tabs.content'), icon: <Type className="h-3.5 w-3.5" /> },
    { key: 'colors', label: t('emailEditor.tabs.colors'), icon: <Palette className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Template gallery - always visible at top */}
      <div className="shrink-0 border-b border-border-default bg-bg-secondary px-4 pt-3 pb-2">
        <label className="mb-2 block text-xs font-medium text-text-secondary">{t('emailEditor.selectTemplate')}</label>
        <TemplateGallery
          selectedTemplate={props.templateType}
          onSelect={props.selectTemplate}
          allowedTemplates={props.allowedTemplates}
        />
      </div>

      {/* Tabs */}
      <div className="shrink-0 px-4 pt-2">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      </div>

      {/* Tab content - scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {activeTab === 'content' && (
          <>
            {visibleFields.has('subject') && (
              <Input
                label={t('emailEditor.subject')}
                value={props.subject}
                onChange={(e) => props.setSubject(e.target.value)}
                placeholder={t('emailEditor.subjectPlaceholder')}
              />
            )}
            {visibleFields.has('greeting') && (
              <Input
                label={t('emailEditor.greeting')}
                value={props.greeting}
                onChange={(e) => props.setGreeting(e.target.value)}
                placeholder={t('emailEditor.greetingPlaceholder')}
              />
            )}
            {visibleFields.has('bodyHtml') && (
              <RichTextArea
                label={t('emailEditor.body')}
                value={props.bodyHtml}
                onChange={props.setBodyHtml}
                placeholder={t('emailEditor.bodyPlaceholder')}
              />
            )}
            {visibleFields.has('cta') && (
              <>
                <Input
                  label={t('emailEditor.ctaText')}
                  value={props.ctaText}
                  onChange={(e) => props.setCtaText(e.target.value)}
                />
                <Input
                  label={t('emailEditor.ctaUrl')}
                  value={props.ctaUrl}
                  onChange={(e) => props.setCtaUrl(e.target.value)}
                  placeholder={t('emailEditor.ctaUrlPlaceholder')}
                  dir="ltr"
                />
              </>
            )}
            <p className="text-xs text-text-muted">{t('emailEditor.variablesHint')}</p>
          </>
        )}

        {activeTab === 'colors' && (
          <div className="space-y-4">
            <ColorPickerField
              label={t('emailEditor.brandColor')}
              value={props.colors.brandColor}
              onChange={(v) => props.setColor('brandColor', v)}
            />
            <ColorPickerField
              label={t('emailEditor.buttonColor')}
              value={props.colors.buttonColor}
              onChange={(v) => props.setColor('buttonColor', v)}
            />
            <ColorPickerField
              label={t('emailEditor.textColor')}
              value={props.colors.textColor}
              onChange={(v) => props.setColor('textColor', v)}
            />
            <ColorPickerField
              label={t('emailEditor.backgroundColor')}
              value={props.colors.backgroundColor}
              onChange={(v) => props.setColor('backgroundColor', v)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
